import type { BigNumber } from '@ethersproject/bignumber';
import { createColumnHelper } from '@tanstack/react-table';
import { type Token } from '@uniswap/sdk-core';
import type { TokenInfo } from '@uniswap/token-lists/src/types';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { determineAutoStatuses } from 'utils/getHarvestFractionInfo';
import { trimLeadingZeroHex } from 'utils/trimLeadingZeroHex';

import useClientSidePagination from 'components/ui/Table/hooks/useClientSidePagination';
import {
  useHarvesterStatuses,
  useStakerPositions
} from 'graphql/api/positions/positions.hooks';
import { useTokensFromMap } from 'hooks/Tokens';
import { usePositions } from 'hooks/usePositions';
import { useCombinedActiveList } from 'state/lists/hooks';

import Button from 'components/ui/Button';
import Table from 'components/ui/Table';
import { TableVariant } from 'components/ui/Table/constants';

import type { TokenLogoData } from 'components/composed/Logo/Logo.types';
import { PoolPositionItem } from 'components/composed/Pools/PoolPositionItem';

type HarvesterStatusMap = { [key: string]: string | undefined };

type MyPositionRow = {
  nonce: BigNumber;
  token0: TokenLogoData;
  token1: TokenLogoData;
  tokenId: BigNumber;
  fee: number;
  liquidity: BigNumber;
  tickLower: number;
  tickUpper: number;
  isAutoHarvesting: boolean;
  isAutoCompounding: boolean;
};

type MyPositionsContentProps = {
  chainId: number;
};

export const MyPositionsContent = ({ chainId }: MyPositionsContentProps) => {
  const navigate = useNavigate();

  const { positions, loading: positionsLoading } = usePositions({ chainId });

  const tokenIDs = positions.map((position) =>
    trimLeadingZeroHex(position.tokenId.toHexString())
  );

  const { data: harvesterStatuses, loading: harvesterStatusesLoading } =
    useHarvesterStatuses({ chainId, ids: tokenIDs });

  const defaultListTokens = useCombinedActiveList();

  const tokensFromMap = useTokensFromMap(defaultListTokens, chainId);

  const harvesterStatusMap = useMemo(() => {
    const validStatuses = harvesterStatuses?.positions || [];

    return validStatuses.reduce<HarvesterStatusMap>((acc, status) => {
      acc[status.id] = status.harvestFraction;
      return acc;
    }, {});
  }, [harvesterStatuses]);

  const { data: stakerPositions, loading: stakerPositionsLoading } =
    useStakerPositions({ chainId });

  const tableData: MyPositionRow[] = positions.map((position) => {
    const token0 = tokensFromMap[position.token0.toUpperCase()] as Token & {
      tokenInfo: TokenInfo;
    };

    const token1 = tokensFromMap[position.token1.toUpperCase()] as Token & {
      tokenInfo: TokenInfo;
    };

    const trimmedTokenIdHex = trimLeadingZeroHex(
      position.tokenId.toHexString()
    );
    const harvestFraction = stakerPositions?.includes(trimmedTokenIdHex)
      ? '0'
      : harvesterStatusMap[trimmedTokenIdHex];
    const { isAutoHarvesting, isAutoCompounding } = determineAutoStatuses({
      harvestFraction
    });

    return {
      nonce: position.nonce,
      token0,
      token1,
      tokenId: position.tokenId,
      fee: position.fee,
      liquidity: position.liquidity,
      tickLower: position.tickLower,
      tickUpper: position.tickUpper,
      isAutoHarvesting,
      isAutoCompounding
    };
  });

  const { paginatedList, ...paginationConfig } =
    useClientSidePagination(tableData);

  const memoizedPaginatedList = useMemo(() => paginatedList, [paginatedList]);

  const columnHelper = createColumnHelper<MyPositionRow>();

  const columns = [
    columnHelper.accessor('tokenId', {
      cell: (info) => {
        const token0 = info.row.original.token0;
        const token1 = info.row.original.token1;
        const fee = info.row.original.fee;
        const liquidity = info.row.original.liquidity;
        const tickLower = info.row.original.tickLower;
        const tickUpper = info.row.original.tickUpper;
        const isAutoHarvesting = info.row.original.isAutoHarvesting;
        const isAutoCompounding = info.row.original.isAutoCompounding;

        return (
          <PoolPositionItem
            tokenData0={token0}
            tokenData1={token1}
            fee={fee}
            liquidity={liquidity}
            tickLower={tickLower}
            tickUpper={tickUpper}
            chainId={chainId}
            isAutoHarvesting={isAutoHarvesting}
            isAutoCompounding={isAutoCompounding}
            harvesterStatusesLoading={
              harvesterStatusesLoading || stakerPositionsLoading
            }
          />
        );
      },
      enableSorting: false,
      meta: {
        className: 'w-full'
      }
    })
  ];

  return (
    <div className='relative flex flex-col bg-blue-900'>
      <span className='mt-2.5 flex items-center justify-center uppercase text-blue-200'>
        Positions ({tableData.length})
      </span>

      <Table
        key='my-pools'
        data={memoizedPaginatedList}
        columns={columns}
        isLoading={positionsLoading}
        placeholder='No current positions'
        showPagination
        paginationConfig={paginationConfig}
        onRowClick={(row) => {
          navigate(`/my-position/${row.tokenId}`);
        }}
        className='mt-4 px-1'
        showHeader={false}
        variant={TableVariant.Secondary}
      />

      <Button
        variant='primary'
        className='absolute -bottom-14 left-1/2 z-11 -ml-px w-41.25 -translate-x-1/2'
        onClick={() => navigate('/add-liquidity')}
      >
        Add Liquidity
      </Button>
    </div>
  );
};
